import React, { useEffect, useState } from 'react';
import { MessageSquare, Search, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import { Modal } from '../../components/Modal';

const statusColors = {
  new: 'bg-purple-100 text-purple-700',
  in_review: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-600',
};

const moodBadges = {
  love: 'text-pink-600 bg-pink-50',
  like: 'text-purple-600 bg-purple-50',
  ok: 'text-blue-600 bg-blue-50',
  confused: 'text-amber-600 bg-amber-50',
  frustrated: 'text-red-600 bg-red-50',
};

const topicLabels = {
  product: 'Product',
  bug: 'Bug Report',
  billing: 'Billing',
  feature: 'Feature Request',
  other: 'Other',
};

const statusOptions = [
  { label: 'All', value: 'all' },
  { label: 'New', value: 'new' },
  { label: 'In review', value: 'in_review' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Archived', value: 'archived' },
];

const moodOptions = ['love', 'like', 'ok', 'confused', 'frustrated'];
const topicOptions = ['product', 'bug', 'billing', 'feature', 'other'];

const AdminFeedbacks = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', mood: 'all', topic: 'all', search: '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [noteModal, setNoteModal] = useState({ open: false, feedback: null, notes: '' });
  const [updating, setUpdating] = useState(false);

  const getErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  };

  useEffect(() => {
    fetchFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 15,
      };
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.mood !== 'all') params.mood = filters.mood;
      if (filters.topic !== 'all') params.topic = filters.topic;
      if (filters.search) params.search = filters.search;

      const { data } = await api.get('/admin/feedbacks', { params });
      if (data.success) {
        setFeedbacks(data.feedbacks);
        setPagination(data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load feedback'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (feedbackId, nextStatus) => {
    try {
      setUpdating(true);
      await api.patch(`/admin/feedbacks/${feedbackId}`, { status: nextStatus });
      toast.success('Feedback updated');
      fetchFeedbacks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to update feedback'));
    } finally {
      setUpdating(false);
    }
  };

  const openNotesModal = (feedback) => {
    setNoteModal({ open: true, feedback, notes: feedback.admin_notes || '' });
  };

  const saveNotes = async () => {
    try {
      setUpdating(true);
      await api.patch(`/admin/feedbacks/${noteModal.feedback.id}`, {
        adminNotes: noteModal.notes,
      });
      toast.success('Notes saved');
      setNoteModal({ open: false, feedback: null, notes: '' });
      fetchFeedbacks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save notes'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <AdminSidebar>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <p className="uppercase text-xs tracking-[0.6em] text-purple-400">Voice of customers</p>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Feedback Inbox</h1>
            <p className="text-gray-600">Review love letters, bug reports, and roadmapping fuel.</p>
          </div>
          <div className="flex gap-3">
            <div className="bg-white px-4 py-2 rounded-2xl shadow">
              <p className="text-xs uppercase tracking-[0.3em] text-gray-400">New</p>
              <p className="text-xl font-bold text-purple-700">
                {feedbacks.filter((f) => f.status === 'new').length}
              </p>
            </div>
            <div className="bg-white px-4 py-2 rounded-2xl shadow">
              <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Active</p>
              <p className="text-xl font-bold text-pink-700">
                {feedbacks.filter((f) => f.status === 'in_review').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <div>
                <label className="text-xs uppercase text-gray-500 font-semibold">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({ ...prev, status: e.target.value }));
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500 font-semibold">Mood</label>
                <select
                  value={filters.mood}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({ ...prev, mood: e.target.value }));
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
                >
                  <option value="all">All moods</option>
                  {moodOptions.map((m) => (
                    <option key={m} value={m}>
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500 font-semibold">Topic</label>
                <select
                  value={filters.topic}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({ ...prev, topic: e.target.value }));
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
                >
                  <option value="all">All topics</option>
                  {topicOptions.map((topic) => (
                    <option key={topic} value={topic}>
                      {topicLabels[topic]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500 font-semibold">Search</label>
                <div className="mt-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => {
                      setPage(1);
                      setFilters((prev) => ({ ...prev, search: e.target.value }));
                    }}
                    placeholder="Name, email, keywords..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center shadow">
            <MessageSquare className="h-10 w-10 mx-auto text-purple-300" />
            <h3 className="text-lg font-semibold text-gray-900 mt-4">No feedback yet</h3>
            <p className="text-gray-500 mt-2">Once families start sending love notes, they’ll show up here.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow overflow-hidden">
            <div className="divide-y divide-gray-100">
              {feedbacks.map((feedback) => {
                const isExpanded = expandedRow === feedback.id;
                return (
                  <div key={feedback.id} className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-gray-900">{feedback.name}</p>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${moodBadges[feedback.mood]}`}>
                            {feedback.mood}
                          </span>
                          <span className="text-xs text-gray-400">{new Date(feedback.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-500">{feedback.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[feedback.status]}`}>
                          {feedback.status.replace('_', ' ')}
                        </span>
                        <button
                          onClick={() => setExpandedRow(isExpanded ? null : feedback.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="mt-4 grid md:grid-cols-5 gap-5">
                        <div className="md:col-span-3 space-y-3">
                          <div className="bg-purple-50 rounded-xl p-4 text-sm text-gray-700 border border-purple-100">
                            {feedback.message}
                          </div>
                          <div className="text-xs text-gray-500">
                            Rating: <span className="font-semibold text-gray-800">{feedback.rating} / 5</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Topic: <span className="font-semibold text-gray-800">{topicLabels[feedback.topic]}</span>
                          </div>
                          {feedback.admin_notes && (
                            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
                              <span className="font-semibold text-gray-700">Admin notes:</span> {feedback.admin_notes}
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-2 space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {['new', 'in_review', 'resolved', 'archived'].map((nextStatus) => (
                              <button
                                key={nextStatus}
                                onClick={() => handleStatusUpdate(feedback.id, nextStatus)}
                                disabled={updating || feedback.status === nextStatus}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                  feedback.status === nextStatus
                                    ? 'border-purple-200 text-purple-500 bg-purple-50'
                                    : 'border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-600'
                                } disabled:opacity-60`}
                              >
                                {nextStatus.replace('_', ' ')}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => openNotesModal(feedback)}
                            className="w-full text-sm font-semibold text-purple-600 border border-purple-200 rounded-lg py-2 hover:bg-purple-50"
                          >
                            {feedback.admin_notes ? 'Update notes' : 'Add notes'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between p-4 text-sm text-gray-600">
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.pages))}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Note Modal */}
      {noteModal.open && (
        <Modal title="Update admin notes" onClose={() => setNoteModal({ open: false, feedback: null, notes: '' })}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{noteModal.feedback?.name}</span> —{' '}
              {noteModal.feedback?.email}
            </p>
            <textarea
              value={noteModal.notes}
              onChange={(e) => setNoteModal((prev) => ({ ...prev, notes: e.target.value }))}
              rows={5}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 text-sm"
              placeholder="Internal context, follow-up actions, etc."
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setNoteModal({ open: false, feedback: null, notes: '' })}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveNotes}
                disabled={updating}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg disabled:opacity-60"
              >
                {updating ? 'Saving...' : 'Save notes'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </AdminSidebar>
  );
};

export default AdminFeedbacks;


